import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, RotateCcw, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { recommendTower } from "../lib/recommend";

const OptionPill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 text-sm font-medium border transition-all duration-200 ${
      active
        ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A1223]"
        : "bg-transparent border-white/15 text-slate-300 hover:border-[#D4AF37]/60 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const TowerRecommender = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({ purpose: "", environment: "", coverage: "", space: "" });
  const [result, setResult] = useState(null);

  const tr = t.recommender;
  const set = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));
  const ready = answers.purpose && answers.environment && answers.coverage && answers.space;

  const groups = [
    {
      key: "purpose", label: tr.q_purpose,
      opts: [
        ["macro", tr.purpose_macro], ["smallcell", tr.purpose_smallcell],
        ["microwave", tr.purpose_microwave], ["broadcast", tr.purpose_broadcast],
      ],
    },
    {
      key: "environment", label: tr.q_environment,
      opts: [
        ["urban", tr.env_urban], ["rural", tr.env_rural],
        ["rooftop", tr.env_rooftop], ["industrial", tr.env_industrial],
      ],
    },
    {
      key: "coverage", label: tr.q_coverage,
      opts: [["low", tr.cov_low], ["med", tr.cov_med], ["high", tr.cov_high]],
    },
    {
      key: "space", label: tr.q_space,
      opts: [["limited", tr.space_limited], ["moderate", tr.space_moderate], ["ample", tr.space_ample]],
    },
  ];

  const compute = () => setResult(recommendTower(answers));
  const reset = () => {
    setResult(null);
    setAnswers({ purpose: "", environment: "", coverage: "", space: "" });
  };

  return (
    <div className="glass p-7 md:p-10 rounded-sm" data-testid="tower-recommender">
      <AnimatePresence mode="wait">
        {!result ? (
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="flex items-center gap-2 text-[#D4AF37] mb-6">
              <Sparkles size={18} />
              <span className="section-label !mb-0">{tr.label}</span>
            </div>
            <div className="space-y-7">
              {groups.map((g) => (
                <div key={g.key}>
                  <p className="text-sm font-semibold text-white mb-3">{g.label}</p>
                  <div className="flex flex-wrap gap-2.5">
                    {g.opts.map(([val, label]) => (
                      <OptionPill
                        key={val}
                        active={answers[g.key] === val}
                        onClick={() => set(g.key, val)}
                        testid={`rec-${g.key}-${val}`}
                      >
                        {label}
                      </OptionPill>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={compute}
              disabled={!ready}
              data-testid="rec-compute-btn"
              className="btn-gold mt-8 inline-flex items-center gap-2 px-7 py-3.5 disabled:opacity-40 disabled:cursor-not-allowed group"
            >
              {tr.getRecommendation}
              <ArrowRight size={18} className={isRTL ? "rotate-180" : ""} />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid="rec-result"
          >
            <div className="flex items-center gap-2 text-[#D4AF37] mb-6">
              <CheckCircle2 size={20} />
              <span className="font-heading text-xl font-bold text-white">{tr.resultTitle}</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#0A1223] border border-white/12 p-5">
                <p className="section-label mb-2">{tr.recTowerType}</p>
                <p className="font-heading text-xl font-bold text-white">{result.towerType}</p>
              </div>
              <div className="bg-[#0A1223] border border-white/12 p-5">
                <p className="section-label mb-2">{tr.recHeight}</p>
                <p className="font-heading text-xl font-bold text-white">{result.height}</p>
              </div>
              <div className="bg-[#0A1223] border border-white/12 p-5">
                <p className="section-label mb-2">{tr.recSolution}</p>
                <p className="text-sm text-slate-300 leading-relaxed">{result.solution}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/request-quotation")}
                data-testid="rec-proceed-btn"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3.5"
              >
                {tr.proceedQuote}
                <ArrowRight size={18} className={isRTL ? "rotate-180" : ""} />
              </button>
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                <RotateCcw size={16} /> {tr.recalculate}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TowerRecommender;
